import type { Tile } from "../engine";
import { evaluateBridgeTile } from "./roadBridgeEvaluate";
import { mapHeight } from "./roadBridgeNeighbors";
import { resolveRoadAutotileMask } from "./roadConnectivity";
import { classifyRoadAutotile } from "./roadStripeKinds";

const COMPUTED_KEY = "__bridges_computed__";

function computeAllBridges({
  tiles,
  width,
  bridges,
}: {
  tiles: readonly Tile[];
  width: number;
  bridges: Map<string, boolean>;
}): void {
  const height = mapHeight(tiles, width);
  const roads: {
    x: number;
    y: number;
    mask: number;
    kind: ReturnType<typeof classifyRoadAutotile>["kind"];
  }[] = [];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (tiles[y * width + x]?.kind !== "road") continue;
      const mask = resolveRoadAutotileMask({ tiles, width, x, y });
      roads.push({ x, y, mask, kind: classifyRoadAutotile(mask).kind });
    }
  }

  for (const { x, y } of roads) bridges.set(`${x},${y}`, false);

  let changed = true;
  while (changed) {
    changed = false;
    for (const { x, y, mask, kind } of roads) {
      const key = `${x},${y}`;
      if (bridges.get(key)) continue;
      if (!evaluateBridgeTile({ tiles, width, x, y, kind, mask, bridges })) continue;
      bridges.set(key, true);
      changed = true;
    }
  }
}

export function isBridgeRoadTile({
  tiles,
  width,
  x,
  y,
  bridgeCache = new Map<string, boolean>(),
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
  bridgeCache?: Map<string, boolean>;
}): boolean {
  if (!bridgeCache.has(COMPUTED_KEY)) {
    computeAllBridges({ tiles, width, bridges: bridgeCache });
    bridgeCache.set(COMPUTED_KEY, true);
  }

  return bridgeCache.get(`${x},${y}`) ?? false;
}
